/**
 * Collection Schema Definitions & Validation
 *
 * All collections use typed SQL columns. No document-mode / fields-based storage.
 */

import type {
  CollectionSchema,
  ColumnDefinition,
  ColumnInterpretation,
  PermissionLevel,
  RolePermissions,
} from '../../shared/types'

export type {
  CollectionSchema,
  ColumnDefinition,
  ColumnInterpretation,
  PermissionLevel,
  RolePermissions,
} from '../../shared/types'

// ============================================================================
// Column Definitions
// ============================================================================

export interface ResolvedColumn {
  id: string
  name: string
  storage: 'number' | 'text'
  interpretation: ColumnInterpretation
  expression?: string
  readonly: boolean
  userBound?: boolean
  immutable?: boolean
  required?: boolean
  default?: unknown
  timestampTrigger?: { field: string; value?: unknown }
}

// ============================================================================
// Column Helpers
// ============================================================================

export function collectionTableName(name: string): string {
  return `c_${name.replace(/[^a-zA-Z0-9_]/g, '_')}`
}

export function columnId(name: string): string {
  return `col_${name.toLowerCase().replace(/[^a-z0-9]/g, '_')}`
}

export function resolveColumn(col: ColumnDefinition): ResolvedColumn {
  const interp: ColumnInterpretation =
    typeof col.interpretation === 'string'
      ? ({ kind: col.interpretation } as ColumnInterpretation)
      : col.interpretation
  return {
    id: col.id ?? columnId(col.name),
    name: col.name,
    storage: col.storage,
    interpretation: interp,
    expression: col.expression,
    readonly: !!col.expression,
    userBound: col.userBound,
    immutable: col.immutable,
    required: col.required,
    default: col.default,
    timestampTrigger: col.timestampTrigger,
  }
}

export function rowToData(
  row: Record<string, unknown>,
  columns: ResolvedColumn[],
): Record<string, unknown> {
  const data: Record<string, unknown> = {}
  for (const col of columns) {
    if (row[col.id] !== undefined && row[col.id] !== null) {
      let val = row[col.id]
      if (col.interpretation.kind === 'json' && typeof val === 'string') {
        try {
          val = JSON.parse(val as string)
        } catch {
          /* keep as string */
        }
      }
      data[col.name] = val
    }
  }
  return data
}

export function dataToColumnValues(
  data: Record<string, unknown>,
  columns: ResolvedColumn[],
): Record<string, unknown> {
  const values: Record<string, unknown> = {}
  for (const col of columns) {
    if (col.expression || col.readonly) continue
    // Record mutations use public schema names only. Accepting physical column
    // ids here lets a second key bypass name-based RBAC/userBound checks.
    const val = data[col.name]
    if (val !== undefined) {
      values[col.id] = coerceValue(val, col.storage, col.interpretation)
    }
  }
  return values
}

export function coerceValue(
  value: unknown,
  storage: 'number' | 'text',
  interpretation: ColumnInterpretation,
): unknown {
  if (value === null || value === undefined) return null
  // An empty string is a value on a text column, not an absence. Folding it
  // into NULL destroyed it on the way in — `rowToData` then dropped the key on
  // the way out — so a field written as `''` read back `undefined` and a
  // `column.default = ''` never materialized at all (it is applied at create
  // and was coerced away one line later). A `number` column has nothing to
  // store for `''`, so there it stays NULL.
  if (value === '') return storage === 'text' ? '' : null

  if (storage === 'text') {
    if (interpretation.kind === 'json') {
      if (typeof value === 'string') return value
      try {
        return JSON.stringify(value)
      } catch {
        return null
      }
    }
    return typeof value === 'string' ? value : String(value)
  }

  if (typeof value === 'number') return isFinite(value) ? value : null
  if (typeof value === 'boolean') return value ? 1 : 0
  if (typeof value !== 'string') return String(value)

  const s = value.trim()

  if (interpretation.kind === 'boolean') {
    const lower = s.toLowerCase()
    if (lower === 'true' || lower === '1' || lower === 'yes') return 1
    if (lower === 'false' || lower === '0' || lower === 'no') return 0
    return s
  }

  if (interpretation.kind === 'percent') {
    const m = s.match(/^(-?\d+(?:\.\d+)?)\s*%$/)
    if (m) return parseFloat(m[1]) / 100
  }

  if (interpretation.kind === 'currency') {
    const cleaned = s.replace(/[^0-9.-]/g, '')
    const n = parseFloat(cleaned)
    if (!isNaN(n)) return n
    return s
  }

  if (interpretation.kind === 'date' || interpretation.kind === 'datetime') {
    const d = new Date(s)
    if (!isNaN(d.getTime())) return epochSeconds(d)
    return s
  }

  const cleaned = s.replace(/[$€£¥,\s]/g, '')
  const n = parseFloat(cleaned)
  if (!isNaN(n) && isFinite(n)) return n

  return s
}

/** Canonical numeric representation for date/datetime storage and triggers. */
export function epochSeconds(date: Date = new Date()): number {
  return Math.floor(date.getTime() / 1000)
}

export function buildTableSelect(collectionName: string, columns: ResolvedColumn[]): string {
  const tbl = collectionTableName(collectionName)
  const parts: string[] = ['_row_id', '_created_by', '_created_at', '_updated_at']
  for (const col of columns) {
    if (col.expression) {
      parts.push(`(${col.expression}) AS "${col.id}"`)
    } else {
      parts.push(`"${col.id}"`)
    }
  }
  return `SELECT ${parts.join(', ')} FROM "${tbl}"`
}

export interface User {
  id: string
  email: string
  name: string
  imageUrl?: string
  role: string
  createdAt: string
  lastSeenAt: string
}

export interface StoredRecord {
  collection: string
  recordId: string
  data: Record<string, unknown>
  createdBy: string
  createdAt: string
  updatedAt: string
}

// ============================================================================
// Permission Checking
// ============================================================================

export interface PermissionContext {
  isTeamMember: (teamId: string, userId: string) => boolean
}

export const noopPermissionContext: PermissionContext = {
  isTeamMember: () => false,
}

export function getRolePermissions(schema: CollectionSchema, role: string): RolePermissions {
  if (schema.permissions[role]) return schema.permissions[role]
  if (schema.permissions['*']) return schema.permissions['*']
  return { read: false, create: false, update: false, delete: false }
}

export function isOwner(
  schema: CollectionSchema,
  record: { data: Record<string, unknown>; createdBy: string },
  userId: string,
): boolean {
  if (schema.ownerField) return record.data[schema.ownerField] === userId
  return record.createdBy === userId
}

function isCollaborator(
  schema: CollectionSchema,
  record: { data: Record<string, unknown> },
  userId: string,
): boolean {
  if (!schema.collaboratorsField) return false
  const collab = record.data[schema.collaboratorsField]
  if (typeof collab === 'string') {
    try {
      return (JSON.parse(collab) as string[]).includes(userId)
    } catch {
      return false
    }
  }
  if (Array.isArray(collab)) return collab.includes(userId)
  return false
}

function passesVisibilityCheck(
  schema: CollectionSchema,
  record: { data: Record<string, unknown> },
): boolean {
  if (!schema.visibilityField) return false
  if (typeof schema.visibilityField === 'string') {
    return record.data[schema.visibilityField] === 'public'
  }
  return record.data[schema.visibilityField.field] === schema.visibilityField.value
}

function checkPermissionLevel(
  level: PermissionLevel,
  schema: CollectionSchema,
  record: { data: Record<string, unknown>; createdBy: string; recordId?: string },
  userId: string,
  ctx: PermissionContext = noopPermissionContext,
): boolean {
  if (level === true) return true
  if (level === false) return false
  if (level === 'own') return isOwner(schema, record, userId)
  if (level === 'unclaimed-or-own') {
    if (schema.ownerField) {
      const ownerVal = record.data[schema.ownerField]
      if (!ownerVal || ownerVal === '') return true
    }
    return isOwner(schema, record, userId)
  }
  if (level === 'collaborator') {
    return isOwner(schema, record, userId) || isCollaborator(schema, record, userId)
  }
  if (level === 'team' || level === 'access') {
    if (isOwner(schema, record, userId)) return true
    if (isCollaborator(schema, record, userId)) return true
    if (schema.teamField) {
      const teamId = record.data[schema.teamField]
      if (typeof teamId === 'string' && ctx.isTeamMember(teamId, userId)) return true
    }
    return false
  }
  if (level === 'published') {
    return isOwner(schema, record, userId) || passesVisibilityCheck(schema, record)
  }
  if (level === 'shared') {
    return (
      isOwner(schema, record, userId) ||
      isCollaborator(schema, record, userId) ||
      passesVisibilityCheck(schema, record)
    )
  }
  return false
}

export function canRead(
  schema: CollectionSchema,
  role: string,
  record: { data: Record<string, unknown>; createdBy: string; recordId?: string },
  userId: string,
  ctx: PermissionContext = noopPermissionContext,
): boolean {
  return checkPermissionLevel(getRolePermissions(schema, role).read, schema, record, userId, ctx)
}

export function canCreate(schema: CollectionSchema, role: string): boolean {
  return getRolePermissions(schema, role).create === true
}

export function canUpdate(
  schema: CollectionSchema,
  role: string,
  record: { data: Record<string, unknown>; createdBy: string; recordId?: string },
  userId: string,
  ctx: PermissionContext = noopPermissionContext,
): boolean {
  return checkPermissionLevel(getRolePermissions(schema, role).update, schema, record, userId, ctx)
}

export function canDelete(
  schema: CollectionSchema,
  role: string,
  record: { data: Record<string, unknown>; createdBy: string; recordId?: string },
  userId: string,
  ctx: PermissionContext = noopPermissionContext,
): boolean {
  return checkPermissionLevel(getRolePermissions(schema, role).delete, schema, record, userId, ctx)
}

/** Check if a caller-supplied field write violates writableFields restrictions. */
export function checkFieldPermissions(
  schema: CollectionSchema,
  role: string,
  newData: Record<string, unknown>,
  existingData?: Record<string, unknown>,
): string | null {
  const perms = getRolePermissions(schema, role)
  if (!perms.writableFields) return null // no restrictions

  const allowedFields = new Set(perms.writableFields)
  for (const key of Object.keys(newData)) {
    if (existingData && newData[key] === existingData[key]) continue
    if (!allowedFields.has(key)) {
      return `Role '${role}' cannot modify field '${key}'`
    }
  }
  return null
}

/**
 * `unclaimed-or-own` lets a client claim an empty owner field, but never lets
 * them choose another user's id. Check the post-write record so the invariant
 * covers both creation and updates while still allowing an owner to unclaim.
 */
export function checkUnclaimedOwnerTransition(
  schema: CollectionSchema,
  role: string,
  nextData: Record<string, unknown>,
  userId: string,
): string | null {
  const permissions = getRolePermissions(schema, role)
  if (permissions.update !== 'unclaimed-or-own' || !schema.ownerField) return null

  const nextOwner = nextData[schema.ownerField]
  if (nextOwner === undefined || nextOwner === null || nextOwner === '' || nextOwner === userId) {
    return null
  }

  return `Role '${role}' can set '${schema.ownerField}' only to their own user id or leave it unclaimed`
}

// ============================================================================
// System-Managed Columns (users collection)
// ============================================================================

/**
 * Columns the system *assigns* — from the identity provider at registration,
 * or from an admin action. A caller who sends one of these has chosen a value
 * and means it, so a record write that would change one is refused loudly
 * rather than silently dropped.
 */
export const SYSTEM_ASSIGNED_COLUMNS = new Set(['email', 'name', 'imageUrl', 'role'])

/**
 * Columns the system *maintains* on its own schedule, without telling anyone.
 * `registerUser` bumps `lastSeenAt` on every WS connect and `handleUserUpdate`
 * rewrites it on every 60s presence heartbeat; neither broadcasts, so every
 * client's copy goes stale silently and there is no heal path short of a
 * refetch. Read-modify-write echo (`put(id, { ...rec.data, field })`) is the
 * SDK's own house style, so refusing these would refuse writes over a value the
 * caller never chose and cannot keep current. They are preserved silently.
 */
export const SYSTEM_MAINTAINED_COLUMNS = new Set(['createdAt', 'lastSeenAt'])

/** Every `users` column owned by the system rather than by record writes. */
export const SYSTEM_MANAGED_COLUMNS = new Set([
  ...SYSTEM_ASSIGNED_COLUMNS,
  ...SYSTEM_MAINTAINED_COLUMNS,
])

/** Standard user columns. Apps spread these into their users schema. */
export const USERS_COLUMNS: ColumnDefinition[] = [
  { name: 'email', storage: 'text', interpretation: 'plain' },
  { name: 'name', storage: 'text', interpretation: 'plain' },
  { name: 'imageUrl', storage: 'text', interpretation: 'plain' },
  { name: 'role', storage: 'text', interpretation: 'plain' },
  { name: 'createdAt', storage: 'text', interpretation: { kind: 'datetime' } },
  { name: 'lastSeenAt', storage: 'text', interpretation: { kind: 'datetime' } },
]

export const BASE_USERS_SCHEMA: CollectionSchema = {
  name: 'users',
  columns: [...USERS_COLUMNS],
  permissions: {
    '*': { read: 'own', create: false, update: false, delete: false },
    admin: { read: true, create: false, update: true, delete: true },
  },
}

// ============================================================================
// Schema Registry
// ============================================================================

/**
 * Lint a CollectionSchema for declarations that look like they should
 * enforce something but don't, due to interactions between top-level
 * fields (visibilityField, ownerField) and per-role permission levels.
 *
 * The SDK has historically silently accepted schemas that imply more
 * enforcement than they actually deliver — e.g., `visibilityField` set
 * but every role's `read: true` means "anyone can read everything"
 * regardless of `visibility`. Warn loudly at registration so app
 * authors notice before shipping a privacy bug.
 *
 * Returns an array of warning messages. Empty = clean.
 */
export function lintSchema(schema: CollectionSchema): string[] {
  const warnings: string[] = []
  const VISIBILITY_AWARE: ReadonlyArray<PermissionLevel> = ['published', 'shared']

  // Roles that grant unconditional read despite visibilityField being declared.
  if (schema.visibilityField) {
    const offendingRoles = Object.entries(schema.permissions)
      .filter(([, perms]) => perms.read === true)
      .map(([role]) => role)
    if (offendingRoles.length > 0) {
      const usesAware = Object.values(schema.permissions).some(
        (p) => typeof p.read === 'string' && VISIBILITY_AWARE.includes(p.read as PermissionLevel),
      )
      if (!usesAware) {
        warnings.push(
          `[${schema.name}] visibilityField is declared but no role uses read: 'published' or 'shared'. ` +
            `Roles with read: true (${offendingRoles.join(', ')}) will see every row regardless of visibility. ` +
            `Change those to read: 'published' (owner OR public) or 'shared' (owner OR collaborator OR public) ` +
            `to actually enforce the filter, or remove visibilityField if you don't intend to gate reads by it.`,
        )
      }
    }
  }

  // An ordinary client role that can create rows must not be able to claim
  // someone else's ownership. Server-written collections legitimately carry
  // a recipient/subject id instead: their non-privileged roles all have
  // create:false, while owner/admin writes are already fully trusted.
  if (schema.ownerField) {
    const col = schema.columns.find((c) => c.name === schema.ownerField)
    const spoofableRoles = Object.entries(schema.permissions)
      .filter(
        ([role, permissions]) =>
          role !== 'owner' &&
          role !== 'admin' &&
          permissions.create === true &&
          permissions.update !== 'unclaimed-or-own',
      )
      .map(([role]) => role)
    if (col && !col.userBound && spoofableRoles.length > 0) {
      warnings.push(
        `[${schema.name}] ownerField is '${schema.ownerField}' but that column is not marked userBound: true. ` +
          `Client role(s) ${spoofableRoles.join(', ')} can create a row with someone else's id in this field, bypassing 'own' permission checks. ` +
          `Add userBound: true (and ideally immutable: true) to the column.`,
      )
    }
  }

  // userBound columns that aren't text storage — userBound sets the
  // field to the caller's userId (a string), so non-text storage is a
  // schema mistake that will surface as a coercion error at write time.
  for (const col of schema.columns) {
    if (col.userBound && col.storage !== 'text') {
      warnings.push(
        `[${schema.name}] column '${col.name}' is userBound but storage is '${col.storage}'. ` +
          `userBound stamps the user id (a string); use storage: 'text'.`,
      )
    }
  }

  return warnings
}

/**
 * Lint a whole schema set: every per-schema warning, plus the rules that
 * need to see the set. The `'team'` permission level resolves membership from
 * a `team_members` collection (`teamId`, `userId`, `status`); without one it
 * denies everything, silently — a mistake that presents as a sync bug, not
 * as a schema error, so name it here.
 */
export function lintSchemas(schemas: CollectionSchema[]): string[] {
  return [...schemas.flatMap((schema) => lintSchema(schema)), ...lintSchemaSet(schemas)]
}

/** The rules that need to see the whole schema set (per-schema rules live in
 *  `lintSchema`; `lintSchemas` composes both). */
export function lintSchemaSet(schemas: CollectionSchema[]): string[] {
  const warnings: string[] = []
  if (!schemas.some((schema) => schema.name === 'team_members')) {
    for (const schema of schemas) {
      const teamRoles = Object.entries(schema.permissions)
        .filter(([, p]) => [p.read, p.update, p.delete].includes('team'))
        .map(([role]) => role)
      if (teamRoles.length > 0) {
        warnings.push(
          `[${schema.name}] role(s) ${teamRoles.join(', ')} use the 'team' permission level, but no ` +
            `'team_members' collection is registered. 'team' resolves membership from team_members ` +
            `(teamId, userId, status), so without it those roles are denied everything — silently. ` +
            `Register a team_members schema or use another level.`,
        )
      }
    }
  }
  return warnings
}

export class SchemaRegistry {
  private trusted: Map<string, CollectionSchema> = new Map()

  constructor(schemas: CollectionSchema[] = []) {
    // registerTrusted runs the per-schema lint; only the set-level rules are
    // this loop's to add.
    for (const w of lintSchemaSet(schemas)) console.warn(`[schema-lint] ${w}`)
    for (const schema of schemas) this.registerTrusted(schema)
  }

  registerTrusted(schema: CollectionSchema): void {
    for (const w of lintSchema(schema)) {
      console.warn(`[schema-lint] ${w}`)
    }
    this.trusted.set(schema.name, schema)
  }

  get(name: string): CollectionSchema | undefined {
    return this.trusted.get(name)
  }

  has(name: string): boolean {
    return this.trusted.has(name)
  }

  all(): CollectionSchema[] {
    return Array.from(this.trusted.values())
  }

  names(): string[] {
    return Array.from(this.trusted.keys())
  }
}
